// Attached to the wrapper around any HotwireCombobox (UI::Form::Select,
// UI::Form::MultiSelect, etc.) so the listbox:
//   1. Escapes overflow-hidden ancestors by using `position: fixed`.
//   2. Flips above the input when there isn't enough room below.
//
// Listens for changes to the input's aria-expanded attribute and to the
// listbox's hidden attribute (HotwireCombobox toggles both), and recomputes
// placement on scroll / resize so the dropdown stays glued to the field.

import { Controller } from "@hotwired/stimulus"

const MARGIN = 4

export default class extends Controller {
  connect() {
    this.fieldset = this.element.matches("fieldset.hw-combobox")
      ? this.element
      : this.element.querySelector("fieldset.hw-combobox")
    if (!this.fieldset) return

    this.input = this.fieldset.querySelector("input[role='combobox']")
    this.wrapper = this.fieldset.querySelector(".hw-combobox__main__wrapper")
    this.listbox = this.fieldset.querySelector("ul.hw-combobox__listbox")
    if (!this.input || !this.wrapper || !this.listbox) return

    this.boundReposition = () => this.reposition()

    this.observer = new MutationObserver(this.boundReposition)
    this.observer.observe(this.input, { attributes: true, attributeFilter: ["aria-expanded"] })
    this.observer.observe(this.listbox, { attributes: true, attributeFilter: ["hidden"] })

    window.addEventListener("scroll", this.boundReposition, true)
    window.addEventListener("resize", this.boundReposition)
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.boundReposition) {
      window.removeEventListener("scroll", this.boundReposition, true)
      window.removeEventListener("resize", this.boundReposition)
    }
  }

  reposition() {
    if (!this.isVisible()) {
      this.resetListbox()
      return
    }

    const wrapperRect = this.wrapper.getBoundingClientRect()
    const listboxHeight = this.listbox.offsetHeight || this.listbox.scrollHeight
    const spaceBelow = window.innerHeight - wrapperRect.bottom
    const spaceAbove = wrapperRect.top
    const placeAbove = spaceBelow < listboxHeight + MARGIN && spaceAbove > spaceBelow

    this.listbox.style.position = "fixed"
    this.listbox.style.width = `${wrapperRect.width}px`
    this.listbox.style.left = `${wrapperRect.left}px`

    if (placeAbove) {
      this.listbox.style.top = "auto"
      this.listbox.style.bottom = `${window.innerHeight - wrapperRect.top + MARGIN}px`
    } else {
      this.listbox.style.bottom = "auto"
      this.listbox.style.top = `${wrapperRect.bottom + MARGIN}px`
    }
  }

  isVisible() {
    return this.input.getAttribute("aria-expanded") === "true" && !this.listbox.hidden
  }

  resetListbox() {
    this.listbox.style.position = ""
    this.listbox.style.top = ""
    this.listbox.style.bottom = ""
    this.listbox.style.left = ""
    this.listbox.style.width = ""
  }
}
